import React, { useMemo, useState, useCallback, memo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, ArrowRight, Sparkles, Lock, AlertCircle, InfoIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/hooks/use-session';
import { useSubscription } from "@/hooks/use-subscription";
import { cn } from "@/lib/utils";
import { RecommendationBadge } from "./shared/RecommendationBadge";
import { StockPick } from "@/types/stock";
import StockItem from "./shared/StockItem";
import PremiumUpgradeSection from "./shared/PremiumUpgradeSection";
import StockPicksLoadingSkeleton from "./shared/StockPicksLoadingSkeleton";

export const StockPicksCard = memo(({ className }: { className?: string }) => {
  const navigate = useNavigate();
  const { session } = useSession();
  const userId = session?.user?.id;

  // Use subscription context instead of direct hook to prevent duplicate calls
  const { isSubscribed, isLoading: isLoadingSubscription } = useSubscription();
  const isPremium = isSubscribed;

  // Fetch stock picks data (dummy data for now)
  // TODO: Replace with actual API call using react-query
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [dataError, setDataError] = useState<Error | null>(null);
  const [marketAnalysis, setMarketAnalysis] = useState<StockPick[]>([]);

  // Simulate data fetching
  useEffect(() => {
    const timer = setTimeout(() => {
      // Simulate success
      setMarketAnalysis([
        { symbol: 'AAPL', companyName: 'Apple Inc.', price: 175.20, change_percent: 1.25, recommendation: 'Buy', confidence: 0.92, rationale: 'Strong earnings report and upcoming product releases drive positive outlook.' },
        { symbol: 'MSFT', companyName: 'Microsoft Corp.', price: 330.50, change_percent: -0.50, recommendation: 'Hold', confidence: 0.75, rationale: 'Stable performance but facing increased regulatory scrutiny.' },
        { symbol: 'TSLA', companyName: 'Tesla, Inc.', price: 780.00, change_percent: 3.10, recommendation: 'Buy', confidence: 0.88, rationale: 'High growth potential in EV market, despite recent volatility.' },
        { symbol: 'AMZN', companyName: 'Amazon.com, Inc.', price: 135.80, change_percent: 0.80, recommendation: 'Hold', confidence: 0.65 },
        { symbol: 'GOOGL', companyName: 'Alphabet Inc.', price: 138.10, change_percent: -0.15, recommendation: 'Sell', confidence: 0.55, rationale: 'Potential impact from AI competition and ad market slowdown.' },
      ]);
      setIsLoadingData(false);
      setDataError(null);

      // Simulate error
      // setDataError(new Error("Failed to fetch stock picks"));
      // setIsLoadingData(false);
    }, 1500); // Simulate 1.5 second delay

    return () => clearTimeout(timer);
  }, []);

  // Determine items to display based on subscription status
  const displayItems = useMemo(() => {
    // Show all if premium, otherwise show the first 2
    return marketAnalysis.slice(0, isPremium ? marketAnalysis.length : 2);
  }, [marketAnalysis, isPremium]);

  // Handlers
  const handleSubscribeClick = useCallback(() => {
    // TODO: Implement navigation or modal logic for subscription upgrade
    console.log("Upgrade button clicked");
    navigate('/settings/subscription'); // Example navigation
  }, [navigate]);

  const handleViewAllClick = useCallback(() => {
    // TODO: Implement navigation to a dedicated stock picks page
    console.log("View All button clicked");
    // navigate('/stock-picks'); // Example navigation
  }, []); // Add dependencies if needed, e.g., navigate

  // Determine loading state
  const isLoading = isLoadingSubscription || isLoadingData;
  const error = dataError;

  if (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return (
      <div className="text-center text-red-400 flex items-center justify-center gap-2 py-4">
        <AlertCircle className="h-5 w-5" />
        <span>Error: {errorMessage}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <Card className={cn("border-white/10 bg-black/30 backdrop-blur-sm flex flex-col", className)}>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-semibold text-white flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-blue-400" />
              Strykr Strategies
            </CardTitle>
            {/* Show View All only if not loading/error and there are items */} 
            {!isLoading && !error && marketAnalysis.length > 0 && (
              <Button 
                variant="ghost"
                size="sm"
                className="text-xs text-blue-400 hover:text-blue-300 px-2"
                onClick={handleViewAllClick}
                disabled={!isPremium && marketAnalysis.length <= 2}
              >
                View All <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            )}
          </div>
          <CardDescription className="text-sm text-muted-foreground">
            Top stock picks based on AI analysis.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-grow pb-2">
          {isLoading ? (
            <StockPicksLoadingSkeleton />
          ) : error ? (
            <div className="text-center text-red-400 flex items-center justify-center gap-2 py-4">
              <AlertCircle className="h-5 w-5" />
              <span>Error: {error instanceof Error ? error.message : String(error)}</span>
            </div>
          ) : marketAnalysis.length > 0 ? (
            <div className="grid grid-cols-1 gap-3">
              {displayItems.map((stock) => (
                <StockItem key={stock.symbol} stock={stock} />
              ))}

              {/* Blurred premium section if not premium and we have more than 2 stocks */} 
              {!isPremium && marketAnalysis.length > 2 && (
                <PremiumUpgradeSection 
                  nextItem={marketAnalysis[2]}
                  totalHidden={marketAnalysis.length - 2} 
                  onSubscribe={handleSubscribeClick} 
                />
              )}
            </div>
          ) : (
            <div className="text-center text-muted-foreground flex items-center justify-center gap-2 py-4">
              <InfoIcon className="h-5 w-5" />
              <span>No stock picks available currently.</span>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Overlay for coming soon */}
      {/* Overlay for coming soon, only over content area */}
      <div className="absolute left-0 right-0 bottom-0 top-[72px] z-10 flex flex-col items-center justify-center bg-black/0 backdrop-blur-md rounded-b-xl border-x-2 border-b-2 border-dashed border-blue-500 pointer-events-auto">
        <Lock className="h-10 w-10 mb-4 text-blue-400 drop-shadow-lg" />
        <div className="text-xl font-semibold text-white mb-2">Strykr AI Stock and Crypto Picks</div>
        <div className="text-sm text-blue-200">Exclusively coming soon for premium users</div>
      </div>
    </div>
  );
});

export default StockPicksCard;
